import { useContext, useMemo, type ReactNode } from 'react';
import { ChatContextType } from '@trinity/chat';
import {
  ActiveChatSessionContext,
  AuthorContext,
  ChatSessionFiltersContext,
} from '@trinity/chat-components/components/Chat/context';
import { ChatStateDispatchContext } from '@trinity/chat-components/components/Chat.State/context';
import { ChatApiClientContext } from '@trinity/chat-components/containers/Chat/contexts';
import { ActiveBrandDetailsContext } from '@trinity/chat-components/containers/UserBrands/UserBrandsContext';
import {
  ChatAPIProxyClient,
  CustomPageRendererContextProvider,
  type CustomPageArbitraryFilterFields,
  type CustomPageDispatchAction,
} from '@trinity/custom-pages-registry';
import { useCustomPageActionDispatch } from '../Chat/hooks/useCustomPageActionDispatch';

const noopCustomPageDispatchAction: CustomPageDispatchAction = () => {};
const noopSetArbitraryFilterFields = (): void => {};

type Props = { children: ReactNode };

export function CustomPageContextContainer({ children }: Props): React.ReactElement {
  const chatApiClient = useContext(ChatApiClientContext);
  const chat = useContext(ActiveChatSessionContext);
  const author = useContext(AuthorContext);
  const activeBrandDetails = useContext(ActiveBrandDetailsContext);
  const filters = useContext(ChatSessionFiltersContext);
  const dispatch = useContext(ChatStateDispatchContext);
  const dispatchCustomPageAction = useCustomPageActionDispatch();

  const resolvedChatAuthenticatedClient = chatApiClient?.authenticatedClient;
  const resolvedSubject = chat?.context.type === ChatContextType.Agent ? chat.context.data.subject : null;
  const resolvedUser = author
    ? {
        identifier: author.identifier,
        name: author.profile.name ?? '',
        email: author.profile.email ?? '',
        metadata: author.metadata,
        brandIndications: author.brandIndications,
      }
    : undefined;
  const resolvedBrand = activeBrandDetails ?? undefined;
  const resolvedArbitraryFilterFields = filters?.arbitraryFields ?? {};

  if (!resolvedUser || !resolvedBrand) {
    throw new Error('CustomPageContextContainer requires user and brand context from chat providers.');
  }

  const customPageProxiesClient = useMemo(
    () => (resolvedChatAuthenticatedClient ? new ChatAPIProxyClient(resolvedChatAuthenticatedClient) : null),
    [resolvedChatAuthenticatedClient]
  );

  const setArbitraryFilterFields = (fields: CustomPageArbitraryFilterFields): void => {
    if (!dispatch) return;

    dispatch({
      type: 'filters/updated',
      payload: {
        filters: {
          arbitraryFields: fields,
        },
      },
    });
  };

  return (
    <CustomPageRendererContextProvider
      value={{
        proxiesClient: customPageProxiesClient,
        subject: resolvedSubject ?? null,
        user: resolvedUser,
        brand: resolvedBrand,
        arbitraryFilterFields: resolvedArbitraryFilterFields,
        setArbitraryFilterFields: setArbitraryFilterFields ?? noopSetArbitraryFilterFields,
        dispatchAction: dispatchCustomPageAction ?? noopCustomPageDispatchAction,
      }}
    >
      {children}
    </CustomPageRendererContextProvider>
  );
}
